import React, { useState } from "react";
import PropTypes from "prop-types";

import { Button } from "semantic-ui-react";

import { FeatureFlag } from "components/FeatureFlag";

import usePublishing from "./hooks";
import { useAutoSaver } from "../../hooks/useAutoSaver";
import { useDeleteCommentDraft } from "../../hooks/useDeleteCommentDraft";

export default function AutosaverWithFeatureFlag(props) {
  return (
    <React.Fragment>
      <FeatureFlag id="comment-filters" showOnDisabled>
        <ClassicAutoSaver {...props} />
      </FeatureFlag>
      <FeatureFlag id="comment-filters">
        <MutatingAutoSaver {...props} />
      </FeatureFlag>
    </React.Fragment>
  );
}

AutosaverWithFeatureFlag.propTypes = {};

export function ClassicAutoSaver(props) {
  return <Autosaver {...props} />;
}

export function MutatingAutoSaver(props) {
  const {
    clientWorkflowUuid,
    comment,
    taskUuid,
    onPublish,
    clientId,
    draftGid,
    createNewComment,
  } = props;

  const isStandaloneDraft = comment?.commentType === "DRAFT";

  // standalone draft should keep related entity until explicitly removed
  const resolvedTaskUuid = isStandaloneDraft ? comment.taskUuid : taskUuid;
  const resolvedCwfUuid = isStandaloneDraft
    ? comment.clientWorkflowUuid
    : clientWorkflowUuid;

  const publishAction = usePublishing(
    createNewComment,
    comment,
    clientId,
    draftGid,
    resolvedTaskUuid,
    resolvedCwfUuid
  );

  return (
    <React.Fragment>
      <Autosaver
        {...props}
        onPublish={async () => {
          return onPublish(publishAction);
        }}
        taskUuid={resolvedTaskUuid}
      />
    </React.Fragment>
  );
}

MutatingAutoSaver.propTypes = {
  draftGid: PropTypes.string,
  comment: PropTypes.object,
  taskUuid: PropTypes.string,
  clientWorkflowUuid: PropTypes.string,
  onPublish: PropTypes.func.isRequired,
  clientId: PropTypes.string,
  createNewComment: PropTypes.bool,
};

MutatingAutoSaver.defaultProps = {
  draftGid: null,
  comment: null,
  taskUuid: null,
  clientWorkflowUuid: null,
  clientId: null,
  createNewComment: true,
};

export function Autosaver({
  comment,
  content,
  commentsLoading,
  onPublish,
  onSave,
  onClose,
  draftGid,
  setDraftGid,
  taskUuid,
  clientWorkflowUuid,
  setLoading,
}) {
  const { deleteDraft, isDeletingDraft } = useDeleteCommentDraft(draftGid);
  const isLoading = commentsLoading || isDeletingDraft;
  const isEmpty = !content || content === "<p><br></p>";
  const { saveDraft, isSavingDraft, message, cancelDelayedSaving } =
    useAutoSaver(
      comment,
      content,
      isLoading,
      draftGid,
      setDraftGid,
      taskUuid,
      clientWorkflowUuid
    );
  const [confirming, setConfirming] = useState(false);

  const wrapAction = (commentAction) => {
    return () => {
      cancelDelayedSaving();
      setLoading();
      return commentAction()
        .then(() => {
          // after action is complete, clear cached draft association from the form
          setDraftGid(null);
        })
        .finally(() => {
          setLoading(false);
          cancelDelayedSaving(); // repeat cancel to catch lagging debounce
        });
    };
  };

  const shouldDisable = isLoading || isSavingDraft;
  return (
    <React.Fragment>
      <Button
        data-test="publish-comment-button"
        loading={isSavingDraft}
        disabled={shouldDisable || isEmpty}
        positive
        content="Publish"
        onClick={wrapAction(onPublish)}
      />
      <Button
        data-test="close-comment-button"
        disabled={shouldDisable || isEmpty}
        color="grey"
        content="Close and Save Draft"
        onClick={wrapAction(async () => {
          return saveDraft(
            content,
            draftGid,
            taskUuid,
            clientWorkflowUuid,
            true
          )
            .then((saveResult) => {
              if (saveResult?.data?.draftGid) {
                setDraftGid(saveResult.data.draftGid);
                return onSave(saveResult);
              }
              return saveResult;
            })
            .then(onClose);
        })}
      />
      <Button
        data-test="reject-edits-button"
        loading={isDeletingDraft}
        disabled={shouldDisable}
        negative
        content={confirming ? "Confirm Discarding" : "Discard"}
        onClick={() => {
          if (confirming || isEmpty) {
            wrapAction(() => {
              if (draftGid && setDraftGid !== null) {
                return deleteDraft().then(() => {
                  setDraftGid(null);
                  return onClose();
                });
              } else {
                return onClose();
              }
            })();
          } else {
            setConfirming(true);
            setTimeout(() => {
              setConfirming(false);
            }, 10000);
          }
        }}
      />
      <div style={{ float: "right" }} data-test="autosaver-label">
        {message}
      </div>
    </React.Fragment>
  );
}

Autosaver.propTypes = {
  content: PropTypes.string.isRequired,
  commentsLoading: PropTypes.bool.isRequired,
  onPublish: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  comment: PropTypes.object,
  draftGid: PropTypes.string,
  setDraftGid: PropTypes.func,
  taskUuid: PropTypes.string,
  clientWorkflowUuid: PropTypes.string,
  setLoading: PropTypes.func.isRequired,
};

Autosaver.defaultProps = {
  onSave: () => {},
  comment: null,
  draftGid: null,
  setDraftGid: () => {},
  taskUuid: null,
  clientWorkflowUuid: null,
};
